import { useQuery } from '@apollo/experimental-nextjs-app-support/ssr'

import { SubscriptionPlanEnum, UseCurrentUser_UsersDocument, UsersRoleEnum } from '@gql/graphql'

export type TTokenTypes =
  | 'achievements'
  | 'coverLetter'
  | 'downloadPdfs'
  | 'interview'
  | 'interviewQuestions'
  | 'jobMatch'
  | 'learnSkills'
  | 'optimizeResume'
  | 'professionalSummary'
  | 'salaryInsights'
  | 'writer'

type TTokens = { [key in TTokenTypes]: number | undefined }

export const useCurrentUser = () => {
  const { data, loading } = useQuery(UseCurrentUser_UsersDocument)
  const user = data?.user
  const paidPlans = [
    SubscriptionPlanEnum.Pro,
    SubscriptionPlanEnum.ProQuarterly,
    SubscriptionPlanEnum.Gold,
    SubscriptionPlanEnum.GoldQuarterly,
  ]
  const isPaidPlan = user?.plan ? paidPlans.includes(user.plan) : false
  const isLifetimePaidUser = isPaidPlan && !user?.hasSubscription
  const profilePictureUrl = user?.profilePictureUrl

  const isAdvisoryOrgAccount = Boolean(user?.isAdvisoryOrgAccount)
  const isAdvisoryClient = Boolean(user?.isAdvisoryClient)
  const isAdvisor = user?.role === UsersRoleEnum.Advisor
  const isAdvisorAdmin = user?.role === UsersRoleEnum.Admin
  const isAdvisoryAccountManager = user?.role === UsersRoleEnum.AccountManager
  const isAdvisoryLeadership = isAdvisor || isAdvisorAdmin || isAdvisoryAccountManager
  const isGoldPaidPlan =
    user?.plan === SubscriptionPlanEnum.Gold || user?.plan === SubscriptionPlanEnum.GoldQuarterly

  const createdAt = user?.createdAt
  const userAccountAge = Date.now() - new Date(createdAt).getTime()
  const userAccountAgeInDays = Math.floor(userAccountAge / (1000 * 60 * 60 * 24))
  const userAccountIsLessThanWeekOld = userAccountAgeInDays <= 7
  const timeLeftForWelcomeOffer = 7 - userAccountAgeInDays

  const tokens: TTokens = {
    achievements: user?.tokenAchievements,
    coverLetter: user?.tokenCoverLetter,
    downloadPdfs: user?.tokenDownloadPdfs,
    interview: user?.tokenInterview,
    interviewQuestions: user?.tokenInterviewQuestions,
    jobMatch: user?.tokenJobMatch,
    learnSkills: user?.tokenLearnSkills,
    optimizeResume: user?.tokenOptimizeResume,
    professionalSummary: user?.tokenProfessionalSummary,
    salaryInsights: user?.tokenSalaryInsights,
    writer: user?.tokenWriter,
  }

  return {
    activeResumesCount: user?.activeResumesCount,
    coverLetterCustomPrompt: user?.coverLetterCustomPrompt,
    experienceLevel: user?.jobExperienceLevel,
    hasCompletedResumeTour: user?.hasCompletedResumeTour,
    hasSeenGoldOnboarding: user?.hasSeenGoldOnboarding,
    isAdvisor,
    isAdvisorAdmin,
    isAdvisoryClient,
    isAdvisoryLeadership,
    isAdvisoryOrgAccount,
    isGoldPaidPlan,
    isLifetimePaidUser,
    isPaidPlan,
    loading,
    location: user?.location,
    plan: user?.plan,
    profilePictureUrl,
    resumeJobAchievementCustomPrompt: user?.resumeJobAchievementCustomPrompt,
    resumeSummaryCustomPrompt: user?.resumeSummaryCustomPrompt,
    role: user?.role,
    targetJobTitle: user?.targetJobTitle,
    timeLeftForWelcomeOffer,
    tokens,
    usedChromeExtension: user?.usedChromeExtension,
    user,
    userAccountAgeInDays,
    userAccountIsLessThanWeekOld,
  }
}
